import {
  Cable,
  FolderOpen,
  ListChecks,
  Radio,
  ShieldCheck,
} from 'lucide-react'
import { Button } from '@/components/ui/button'

type DesktopStep = {
  number: string
  title: string
  label: string
  image: string
  alt: string
  body: string[]
  points: string[]
  icon: typeof FolderOpen
}

const steps: DesktopStep[] = [
  {
    number: '01',
    title: 'Start with a known channel plan',
    label: 'Starting workflow',
    image: '/desktop/01-start.png',
    alt: 'NewChirp Desktop welcome screen with options to open a CSV or connect a radio',
    body: [
      'The desktop app opens with a clear choice: load an existing CHIRP-compatible CSV, or connect a programming cable and read the radio.',
      'Use New for a blank configuration or the built-in GMRS starter. Open CSV is the best path when moving a channel plan between desktop and mobile.',
    ],
    points: [
      'A CSV can be prepared before any radio is connected.',
      'The GMRS starter creates an editable 22-channel template.',
      'Writes stay locked until a matching radio image has been read.',
    ],
    icon: FolderOpen,
  },
  {
    number: '02',
    title: 'Connect and read before writing',
    label: 'Radio connection',
    image: '/desktop/02-radio-connection.png',
    alt: 'NewChirp Desktop radio connection screen with serial port, model, and transfer controls',
    body: [
      'Choose the serial port, refresh after connecting the cable, and select the exact radio model.',
      'Choose Read from radio to import the current memory image. NewChirp keeps that verified image tied to the connection and model so a later write cannot use the wrong backup.',
    ],
    points: [
      'Power on the radio before refreshing the serial port list.',
      'Select the exact model family before reading or writing.',
      'Keep the cable connected through the full transfer.',
    ],
    icon: Cable,
  },
  {
    number: '03',
    title: 'Review and edit the channel library',
    label: 'Channel library',
    image: '/desktop/03-channel-library.png',
    alt: 'NewChirp Desktop GMRS channel library showing the 22-channel starter list',
    body: [
      'The library summarizes the active channels, scan-enabled memories, and validation issues. Search by name, frequency, or note, then duplicate or add rows as needed.',
      'The status banner tells you whether a matching radio image is ready. Edits remain in the local workspace until you intentionally save or write them.',
    ],
    points: [
      'Use the validation count to catch incomplete memories.',
      'Duplicate is useful for repeater pairs and regional variations.',
      'Save a CSV whenever you want a durable local backup.',
    ],
    icon: ListChecks,
  },
  {
    number: '04',
    title: 'Check settings and frequency plans',
    label: 'Reference and settings',
    image: '/desktop/04-frequency-reference.png',
    alt: 'NewChirp Desktop GMRS and FRS frequency reference table',
    body: [
      'Radio Settings exposes controls supplied by the selected radio driver, including squelch, backlight, VOX, and other model-specific options.',
      'The Frequency Reference provides a printable GMRS/FRS channel plan with power limits, bandwidth, and notes when planning a fresh configuration.',
    ],
    points: [
      'Settings are model-dependent and appear after a compatible radio image is available.',
      'Print the reference when planning channels away from the computer.',
      'Return to Radio Connection when you are ready to write the verified image.',
    ],
    icon: Radio,
  },
]

export function DesktopGuide() {
  return (
    <section id="desktop" className="scroll-mt-20 border-b border-border/60 py-12 md:py-16">
      <div className="mx-auto max-w-6xl">
        <div className="max-w-3xl">
          <span className="font-mono text-sm text-primary">// desktop guide</span>
          <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight sm:text-4xl">
            NewChirp Desktop: from channel plan to a safe write
          </h2>
          <p className="mt-4 text-pretty leading-relaxed text-muted-foreground">
            Follow the real desktop workflow for loading a configuration, reading a radio,
            reviewing channels, and preparing a safe upload.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button nativeButton={false} render={<a href="/#download" />}>
              Download NewChirp Desktop
            </Button>
            <Button variant="outline" nativeButton={false} render={<a href="/beginners-guide" />}>
              Read the beginner guide
            </Button>
          </div>
          <p className="mt-3 font-mono text-xs text-muted-foreground">
            Windows · macOS · Linux · Screenshots from NewChirp Desktop 1.0.6
          </p>
        </div>

        <div className="mt-14 flex flex-col gap-16 md:gap-24">
          {steps.map((step, index) => {
            const Icon = step.icon
            const imageFirst = index % 2 === 0
            return (
              <article
                key={step.number}
                className="grid items-center gap-8 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] lg:gap-14"
              >
                <figure className={imageFirst ? 'lg:order-1' : 'lg:order-2'}>
                  <div className="overflow-hidden rounded-xl border border-border bg-card shadow-2xl shadow-primary/10">
                    <img
                      src={step.image}
                      alt={step.alt}
                      loading={index === 0 ? 'eager' : 'lazy'}
                      className="h-auto w-full"
                      width={1280}
                      height={720}
                    />
                  </div>
                  <figcaption className="mt-3 text-center font-mono text-[11px] text-muted-foreground">
                    NewChirp Desktop 1.0.6 · {step.label}
                  </figcaption>
                </figure>

                <div className={imageFirst ? 'lg:order-2' : 'lg:order-1'}>
                  <span className="flex items-center gap-2 font-mono text-xs font-semibold text-primary">
                    <Icon className="h-4 w-4" />
                    STEP {step.number}
                  </span>
                  <h3 className="mt-3 text-balance text-2xl font-bold tracking-tight sm:text-3xl">
                    {step.title}
                  </h3>
                  <div className="mt-4 flex flex-col gap-3 leading-relaxed text-muted-foreground">
                    {step.body.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                  <ul className="mt-5 flex flex-col gap-2.5 rounded-xl border border-primary/20 bg-primary/5 p-4">
                    {step.points.map((point) => (
                      <li key={point} className="flex items-start gap-3 text-sm">
                        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            )
          })}
        </div>

        <div className="mt-16 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6">
          <h3 className="font-semibold text-foreground">Save the original radio image</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Before writing changes, save the original read as a backup. If a write is interrupted,
            that saved image is the fastest path back to the radio&apos;s previous configuration.
          </p>
        </div>
      </div>
    </section>
  )
}
